import { create } from '@bufbuild/protobuf';
import { MCPSchema, MCPTransport } from '@/proto/agentplatform/v1/mcp_pb';
import { MCPClassSchema, type MCPClass } from '@/proto/agentplatform/v1/mcp_pb';
import { ParamSchema, type Param } from '@/proto/agentplatform/v1/param_pb';

export interface HeaderRow {
  key: string;
  value: string;
  configurable: boolean;
}

export interface UrlSegment {
  value: string;
  configurable: boolean;
  paramName: string;
  defaultValue: string;
}

export interface MCPClassFormState {
  id?: string;
  name: string;
  description: string;
  serverUrl: string;
  urlSegments: UrlSegment[];
  transport: MCPTransport;
  headers: HeaderRow[];
  otherParams: Param[];
  skipTlsVerify: boolean;
  timeoutSeconds: number;
}

export const HEADER_TARGET_PREFIX = 'headers.';

export const emptyMCPClassForm: MCPClassFormState = {
  name: '',
  description: '',
  serverUrl: '',
  urlSegments: [],
  transport: MCPTransport.MCP_TRANSPORT_STREAMABLE_HTTP,
  headers: [],
  otherParams: [],
  skipTlsVerify: false,
  timeoutSeconds: 0,
};

export function parseUrlSegments(url: string): UrlSegment[] {
  const match = url.match(/^(https?:\/\/)(.*)/);
  if (!match) return [];
  const rest = match[2] || '';
  const segments = rest.split('/').filter(Boolean);
  return segments.map((seg) => {
    const paramMatch = seg.match(/^\$\((.+)\)$/);
    if (paramMatch) {
      return { value: seg, configurable: true, paramName: paramMatch[1], defaultValue: '' };
    }
    return { value: seg, configurable: false, paramName: seg, defaultValue: seg };
  });
}

function getUrlProtocol(url: string): string {
  const match = url.match(/^(https?:\/\/)/);
  return match ? match[1] : '';
}

function buildServerUrl(protocol: string, segments: UrlSegment[]): string {
  if (segments.length === 0) return protocol;
  const path = segments.map((s, i) => (s.configurable ? `$(param_${i})` : s.value)).join('/');
  return `${protocol}${path}`;
}

export function fromMCPClass(cls: MCPClass): MCPClassFormState {
  const tpl = cls.template;
  const headerParamTargets = new Set(
    (cls.params || [])
      .filter((p) => p.target && p.target.startsWith(HEADER_TARGET_PREFIX))
      .map((p) => p.target)
  );
  const serverUrl = tpl?.serverUrl || '';
  const urlParamValues = new Map(
    (cls.params || [])
      .filter((p) => p.target && p.target.startsWith('server_url.'))
      .map((p) => [p.name, p.paramValue?.value ?? ''])
  );
  const urlSegments = parseUrlSegments(serverUrl).map((seg, i) => {
    if (seg.configurable) {
      const paramName = `param_${i}`;
      return {
        ...seg,
        defaultValue: urlParamValues.get(paramName) ?? urlParamValues.get(seg.paramName) ?? '',
      };
    }
    return seg;
  });
  return {
    id: cls.id,
    name: cls.name,
    description: cls.description,
    serverUrl,
    urlSegments,
    transport: tpl?.transport ?? MCPTransport.MCP_TRANSPORT_STREAMABLE_HTTP,
    headers: Object.entries(tpl?.headers || {}).map(([key, value]) => ({
      key,
      value,
      configurable: headerParamTargets.has(`${HEADER_TARGET_PREFIX}${key}`),
    })),
    otherParams: (cls.params || []).filter(
      (p) =>
        !p.target ||
        (!p.target.startsWith(HEADER_TARGET_PREFIX) && !p.target.startsWith('server_url.'))
    ),
    skipTlsVerify: tpl?.skipTlsVerify ?? false,
    timeoutSeconds: tpl?.timeoutSeconds ?? 0,
  };
}

export function toMCPClass(form: MCPClassFormState): MCPClass {
  const headers: Record<string, string> = {};
  for (const { key, value } of form.headers) {
    if (key.trim()) headers[key.trim()] = value;
  }
  const serverUrl =
    form.urlSegments.length > 0
      ? buildServerUrl(getUrlProtocol(form.serverUrl.trim()), form.urlSegments)
      : form.serverUrl.trim();
  const template = create(MCPSchema, {
    name: form.name.trim(),
    description: form.description,
    serverUrl,
    transport: form.transport,
    headers,
    skipTlsVerify: form.skipTlsVerify,
    timeoutSeconds: Math.max(0, Math.floor(form.timeoutSeconds || 0)),
  });
  const headerParams: Param[] = form.headers
    .filter((h) => h.configurable && h.key.trim())
    .map((h) =>
      create(ParamSchema, {
        type: 'string',
        name: h.key.trim(),
        target: `${HEADER_TARGET_PREFIX}${h.key.trim()}`,
        configurable: true,
        paramValue: { case: 'value', value: h.value },
      })
    );
  const urlParams: Param[] = [];
  form.urlSegments.forEach((s, i) => {
    if (s.configurable) {
      const paramName = `param_${i}`;
      urlParams.push(
        create(ParamSchema, {
          type: 'string',
          name: paramName,
          target: `server_url.${paramName}`,
          configurable: true,
          paramValue: { case: 'value', value: s.defaultValue },
        })
      );
    }
  });
  return create(MCPClassSchema, {
    id: form.id,
    name: form.name.trim(),
    description: form.description,
    template,
    params: [...form.otherParams, ...headerParams, ...urlParams],
  });
}

export function isMCPClassFormValid(form: MCPClassFormState): boolean {
  return !!form.name.trim() && !!form.serverUrl.trim();
}
